import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Loader2, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

export default function ForgotPasswordPage() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError('Please enter your email address.');
    setLoading(true);
    try {
      await forgotPassword(email.trim());
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-xl mx-auto py-4 fade-in">
        <div className="bg-white rounded-3xl border-2 border-success/30 shadow-sm p-8 md:p-10 text-center">
          <div className="w-16 h-16 bg-success/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-success" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl md:text-3xl text-text mb-3">
            Check your inbox
          </h1>
          <p className="text-text-muted text-lg mb-6">
            If an account exists for <strong className="text-text">{email}</strong>, we've sent a password reset link. It will arrive in a few minutes.
          </p>
          <p className="text-text-muted text-base mb-6">
            The link is valid for 1 hour. If you don't see the email, please check your spam or junk folder.
          </p>
          <Link to="/login">
            <Button className="rounded-xl py-5 px-8 text-lg font-bold">Back to sign in</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto py-4 fade-in">
      <div className="bg-white rounded-3xl border-2 border-stone-200 shadow-sm p-8 md:p-10">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-primary/10 rounded-full mx-auto mb-4 flex items-center justify-center">
            <KeyRound className="w-7 h-7 text-primary" />
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text mb-2">
            Reset your password
          </h1>
          <p className="text-text-muted text-lg">
            Enter your email and we'll send you a reset link.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="block text-base font-semibold text-text mb-2">Email address</label>
            <div className="relative">
              <Mail className="w-5 h-5 text-text-muted absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="forgot-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-4 text-lg rounded-xl border-2 border-stone-200 focus:border-primary focus:outline-none"
                placeholder="you@example.com"
              />
            </div>
          </div>

          {error && (
            <div data-testid="forgot-error" className="bg-error/10 text-error border border-error/30 rounded-xl p-4 text-base">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-primary text-white hover:bg-primary-600 rounded-xl py-6 text-lg font-bold">
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
            Send reset link
          </Button>
        </form>

        <div className="mt-6 pt-6 border-t border-stone-200 text-center">
          <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="back-to-login">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
